import type {
  KfmlNameMatch,
  KfmlProjectTodos,
  KfmlTodoItem,
  KfmlTodoList,
  Phase,
  Project,
} from '@projektverwaltung/shared';
import { canDoProjectAction } from '@projektverwaltung/shared';
/**
 * V1.5 Übergaben-Sicht — zentrale Meeting-Sicht für KL (§10.18).
 * Quelle: `design-prototyp/10.18-uebergaben-sicht-design.md`.
 *
 * Zwei Sektionen je Kundensegment:
 *   - Eingehend (Phase=Neu) → Action "→ Move auf Setup"
 *   - Ausgehend (Phase=Handover) → Action "→ Projekt abschließen"
 *
 * HandoverCard rendert AssignableRow×3 + HandoverForm + StaffingMaske +
 * Action-Button. "besprochen"-Häkchen ist UI-only (sessionStorage).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/toast.js';
import { AssignableRow } from '../../components/v2/assignable-row.js';
import { BEFUNDE_QUERY_KEY } from '../../components/v2/befunde/befund-basis.js';
import { BriefingExportActions } from '../../components/v2/briefing-export-actions.js';
import { Card } from '../../components/v2/card.js';
import { useConfirm } from '../../components/v2/confirm-dialog.js';
import { HandoverForm, OUTBOUND_FIELDS } from '../../components/v2/handover-form.js';
import { HandoverSfFilePanel } from '../../components/v2/handover-sf-file-panel.js';
import { IntakeNotesBlock } from '../../components/v2/intake-notes-block.js';
import { KfmlWorklistToggle } from '../../components/v2/kfml-worklist-toggle.js';
import { KlFollowupDateEditor } from '../../components/v2/kl-followup-date-editor.js';
import { ResponsibilityPicker } from '../../components/v2/responsibility-picker.js';
import { SegmentNotesBlock } from '../../components/v2/segment-notes-block.js';
import { SFInfoCard } from '../../components/v2/sf-info-card.js';
import { StaffingMaske } from '../../components/v2/staffing-maske/staffing-maske.js';
import { ApiError, api } from '../../lib/api.js';
import { useCanAssignLeadManager, useStaffingPermissions } from '../../lib/permissions.js';
import { useReaderMode } from '../../lib/reader.js';
import { useSession } from '../../lib/session.js';
import { resolveStartValue } from '../../lib/sticky-filters.js';
import {
  type EingangReihenfolgeHandlers,
  REIHENFOLGE_ENDE_ZIEL,
  useEingangReihenfolge,
} from '../../lib/use-eingang-reihenfolge.js';
import { useStickyFilters } from '../../lib/use-sticky-filters.js';

/**
 * Seit #63 liegt die Segmentwahl am Konto (`view_filters.handover.segment`) und ist
 * damit geräteübergreifend. Der lokale Schlüssel wird nur noch GELESEN: als
 * Startwert vor dem Laden der Präferenzen und als einmalige Übernahmequelle für
 * Bestandsnutzer.
 */
const SEGMENT_STORAGE_KEY = 'pv:handover-segment';
const REVIEWED_STORAGE_PREFIX = 'pv:handover-reviewed:';
const STAFFING_OPEN_STORAGE_PREFIX = 'pv:handover-staffing-open:';

type SegmentKey = string;

export function HandoverPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmAction = useConfirm();
  const { user } = useSession();
  // Umsortieren des Eingangs (#115): Admin ODER kaufm. Leitung — dasselbe Muster
  // wie der manuelle Re-Sync in settings.tsx (dort ebenfalls Admin/kaufm_leitung).
  const canReorder =
    !!user && (user.is_admin || user.function_roles.some((r) => r.role === 'kaufm_leitung'));
  const reihenfolge = useEingangReihenfolge({ canReorder });
  // Default-Segment = eigenes BL-Segment (statt alphabetisch erstes), abänderbar + persistiert.
  const ownSegment =
    (user?.function_roles ?? []).find((fr) => fr.role === 'bereichsleiter' && fr.scope_segment)
      ?.scope_segment ?? null;
  const projectsQuery = useQuery({
    queryKey: ['v2-handover-projects'],
    queryFn: () => api.listProjects({}) as Promise<{ items: Project[] }>,
  });
  const phasesQuery = useQuery({ queryKey: ['v2-phases'], queryFn: () => api.listPhases() });

  const allActive = useMemo(
    () => (projectsQuery.data?.items ?? []).filter((p) => p.lifecycle_status === 'active'),
    [projectsQuery.data],
  );

  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });

  // Für Funktionsrolle `mtb` beim Öffnen auf die eigene Person vorbelegt, sonst leer.
  // Seit #63/#65 wird die Auswahl gemerkt — mit derselben Regel wie die Linse des Phase
  // Board: eine getroffene Wahl gewinnt immer, die Vorbelegung ist nur Startwert und
  // zählt selbst NICHT als Wahl. Deshalb `mtbChosen`: ohne diese Unterscheidung würde
  // entweder jeder Erstbesuch die Vorbelegung als Wahl festschreiben (dann ist sie tot,
  // auch wenn die Rolle später wechselt) oder die bewusste Wahl „alle" (null) wäre von
  // „nie gewählt" ununterscheidbar — dann käme die Vorbelegung bei jedem Aufruf zurück.
  const isMtbRole = (user?.function_roles ?? []).some((fr) => fr.role === 'mtb');
  const mtbPreset = isMtbRole ? (user?.user_id ?? null) : null;
  const [mtbFilter, setMtbFilter] = useState<string | null>(mtbPreset);
  const [mtbChosen, setMtbChosen] = useState(false);
  /** Verhindert, dass der Startwert nach der Hydration ein zweites Mal greift. */
  const mtbStartApplied = useRef(false);
  /**
   * `consume: false` heißt „setz den Startwert, aber verbrauche den Erstbesuch
   * NICHT" — dieselbe Trennung wie in `decidePersonaDefaults` (`mark: !failed`),
   * und aus demselben Grund:
   *
   * Ein gescheiterter Präferenz-Abruf darf die Hydration nicht aufbrauchen.
   * Sonst passiert Folgendes, ohne dass der Nutzer irgendetwas tut: Abruf
   * scheitert → Vorbelegung greift und verbraucht den Start → ein späterer
   * Refetch (Tabwechsel, `refetchOnWindowFocus`) gelingt → `apply` überspringt
   * den MTB-Zweig wegen des Ref → `mtbChosen` bleibt `false` → der Snapshot
   * enthält kein `mtb` mehr → der nächste Save löscht die gemerkte Auswahl am
   * Konto. Ein Doppellauf ist dagegen harmlos: bei getroffener Wahl steigt die
   * Funktion früh aus, sonst schreibt sie denselben Preset-Wert.
   */
  const applyStartMtb = useCallback(
    (remembered: string | null | undefined, consume = true) => {
      if (consume) mtbStartApplied.current = true;
      // Der Nutzer kann schneller sein als die Präferenz-Anfrage. Hat er schon
      // gewählt, gewinnt seine Wahl — sonst würde sie hier still zu „keine Wahl"
      // degradiert: weiter angezeigt, aber nie gespeichert.
      if (mtbChosen) return;
      const start = resolveStartValue(remembered, mtbPreset);
      setMtbFilter(start.value ?? null);
      setMtbChosen(start.chosen);
    },
    [mtbPreset, mtbChosen],
  );
  /** Jede Änderung über die Filterleiste ist eine Wahl — auch das Zurücksetzen auf „alle". */
  const chooseMtb = useCallback((next: string | null) => {
    setMtbFilter(next);
    setMtbChosen(true);
  }, []);

  // Auswahl = die als MTB zugewiesenen Personen aus den aktiven Projekten.
  const mtbOptions = useMemo(() => {
    const nameById = new Map(
      (usersQuery.data?.items ?? []).map((u) => [u.user_id, u.name] as const),
    );
    const ids = new Set<string>();
    for (const p of allActive) if (p.mtb_user_id) ids.add(p.mtb_user_id);
    return Array.from(ids)
      .map((id) => ({ value: id, label: nameById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allActive, usersQuery.data]);

  const mtbFilterLabel = mtbFilter
    ? (mtbOptions.find((o) => o.value === mtbFilter)?.label ??
      // Default-auf-sich kann greifen, bevor der eigene Name in mtbOptions auftaucht
      // (z. B. kein aktives Projekt als MTB / usersQuery noch nicht geladen) → eigenen
      // Namen nutzen statt der rohen UUID.
      (mtbFilter === user?.user_id ? user?.name : undefined) ??
      mtbFilter)
    : null;

  const phases = (phasesQuery.data?.items ?? []).slice().sort((a, b) => a.order - b.order);
  const neuPhase = phases.find((p) => p.name === 'Neu');
  const handoverPhase = phases.find((p) => p.name === 'Handover');
  const setupPhase = phases.find((p) => p.name === 'Setup');

  const segments = useMemo(() => {
    const set = new Set<string>();
    for (const p of allActive) if (p.segment) set.add(p.segment);
    return Array.from(set).sort();
  }, [allActive]);

  const [localSegment] = useState<string | undefined>(
    () => window.localStorage.getItem(SEGMENT_STORAGE_KEY) ?? undefined,
  );
  const [segment, setSegment] = useState<SegmentKey>(() => localSegment ?? '');

  useEffect(() => {
    if (segments.length === 0) return;
    if (!segment || !segments.includes(segment)) {
      const preferred =
        ownSegment && segments.includes(ownSegment) ? ownSegment : (segments[0] ?? '');
      setSegment(preferred);
    }
  }, [segments, segment, ownSegment]);

  // Sticky Filters (#63): Segment- und MTB-Auswahl geräteübergreifend am Konto.
  // Die Freitext-Suche bleibt bewusst draußen — gemerkt sähe sie beim nächsten
  // Aufruf wie eine leere Liste aus.
  const sticky = useStickyFilters('handover', {
    values: {
      segment,
      // NUR bei getroffener Wahl mitschicken (analog zu `lens` im Phase Board):
      // ein fehlender Schlüssel wird beim Merge nicht geschrieben, und genau das
      // hält die Vorbelegung am Leben. Diese Zeile und der `mtbChosen`-Riegel in
      // `applyStartMtb` müssen im Gleichschritt bleiben.
      // Netz seit Issue #64: `handover-sticky.test.tsx` rendert diese Seite echt
      // (jsdom + Testing-Library) und wird rot, wenn die Bedingung hier wegfällt.
      ...(mtbChosen ? { mtb: mtbFilter } : {}),
    },
    // Erst mitschreiben, wenn die Segmentliste steht: bis dahin ist `segment` der
    // leere Startwert, den der Korrektur-Effekt oben gleich ersetzt. Ohne das Gate
    // ginge beim Erstbesuch erst `{segment:''}` und unmittelbar danach der
    // korrigierte Wert ans Konto.
    enabled: segments.length > 0,
    // Bestandsnutzer: bisher lokal gemerktes Segment einmalig ans Konto ziehen.
    // Für `mtb` gibt es keine lokale Altablage — der Filter war nie persistiert.
    local: { segment: localSegment },
    apply: (f) => {
      if (f.segment) setSegment(f.segment);
      if (!mtbStartApplied.current) applyStartMtb(f.mtb);
    },
  });

  // Zwei Wege führen hierher, ohne dass `apply` je läuft: am Konto liegt für diese
  // Sicht noch nichts (`hydrated` ohne `hadStored`), oder die Präferenz-Anfrage ist
  // gescheitert. In beiden Fällen muss der Startwert trotzdem greifen — sonst hinge
  // die Rollen-Vorbelegung am Erfolg eines Netzwerkaufrufs.
  //
  // Der Unterschied zwischen beiden steckt im zweiten Argument: nur der echte
  // Erstbesuch verbraucht die Hydration. Nach einem Fehler bleibt sie offen, damit
  // ein späterer erfolgreicher Refetch die gemerkte Auswahl noch anwenden kann.
  useEffect(() => {
    if (mtbStartApplied.current) return;
    if (sticky.hydrated && !sticky.hadStored) applyStartMtb(undefined);
    else if (sticky.failed) applyStartMtb(undefined, false);
  }, [sticky.failed, sticky.hydrated, sticky.hadStored, applyStartMtb]);

  const inSegment = allActive.filter((p) => p.segment === segment);
  const matchesMtb = (p: Project) => !mtbFilter || p.mtb_user_id === mtbFilter;
  // Reihenfolge des Eingangs (#115): ausschließlich die Server-Reihenfolge, keine
  // eigene Sortierlogik hier — sonst driftet die Anzeige von dem ab, was ein
  // Ziehvorgang tatsächlich bewirkt.
  const incoming = reihenfolge.sortiere(
    neuPhase ? inSegment.filter((p) => p.phase_id === neuPhase.phase_id).filter(matchesMtb) : [],
  );
  const outgoing = handoverPhase
    ? inSegment.filter((p) => p.phase_id === handoverPhase.phase_id).filter(matchesMtb)
    : [];
  const totalAllSegments = allActive.filter(
    (p) =>
      (neuPhase && p.phase_id === neuPhase.phase_id) ||
      (handoverPhase && p.phase_id === handoverPhase.phase_id),
  ).length;
  const countInSegment = incoming.length + outgoing.length;

  // KFML-Todoliste (Issue #14, WP-7): on-demand je Segment. Lese-/Erzeugen-Aktion
  // ohne Schreiben am Projekt → auch für Reader nutzbar (KFML/KL-Sicht).
  const [kfmlResult, setKfmlResult] = useState<KfmlTodoList | null>(null);
  const kfmlMut = useMutation({
    mutationFn: (seg: string) => api.kfmlTodos(seg),
    onSuccess: (data) => setKfmlResult(data),
    onError: (err) =>
      toast.error(
        t('v2.kfml.toast_failed', 'Erstellen der KFML-Todos fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-6">
      <Header count={countInSegment} segment={segment} />

      <SegmentChipBar
        segments={segments}
        active={segment}
        onChange={setSegment}
        totalAllSegments={totalAllSegments}
      />

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => kfmlMut.mutate(segment)}
          disabled={!segment || kfmlMut.isPending}
          className="text-xs px-3 py-2 rounded-md"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: !segment || kfmlMut.isPending ? 0.5 : 1,
            cursor: !segment || kfmlMut.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {kfmlMut.isPending
            ? t('v2.kfml.btn_creating', 'Erstellt…')
            : t('v2.kfml.btn_create', 'KFML-Todos für KuSe erstellen')}
        </button>

        <div className="flex items-center gap-2">
          <label
            htmlFor="handover-mtb-filter"
            className="text-xs"
            style={{ color: 'var(--ink-3)' }}
          >
            {t('v2.handover.mtb_filter_label', 'MTB')}
          </label>
          <select
            id="handover-mtb-filter"
            value={mtbFilter ?? ''}
            onChange={(e) => chooseMtb(e.target.value || null)}
            className="text-sm rounded-md px-2 py-1"
            style={{
              background: 'var(--surface-2)',
              color: 'var(--ink-1)',
              border: `1px solid ${mtbFilter ? 'var(--primary)' : 'var(--line-soft)'}`,
            }}
          >
            <option value="">{t('v2.handover.mtb_filter_all', 'Alle MTB')}</option>
            {mtbOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {mtbFilter && (
            <button
              type="button"
              onClick={() => chooseMtb(null)}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md"
              style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}
              title={t('v2.handover.mtb_filter_clear', 'MTB-Filter entfernen')}
            >
              {t('v2.handover.mtb_filter_active', 'Gefiltert: {{name}}', { name: mtbFilterLabel })}
              <span aria-hidden>✕</span>
            </button>
          )}
        </div>
      </div>

      {(kfmlMut.isPending || kfmlResult) && (
        <KfmlResultPanel
          segment={segment}
          isLoading={kfmlMut.isPending}
          result={kfmlResult}
          onClose={() => setKfmlResult(null)}
        />
      )}

      {projectsQuery.isLoading || phasesQuery.isLoading ? (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.handover.loading', 'Lädt…')}
          </p>
        </Card>
      ) : countInSegment === 0 ? (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.handover.empty', 'Für Segment {{segment}} aktuell keine Übergaben offen.', {
              segment,
            })}
          </p>
        </Card>
      ) : (
        <div className="space-y-8">
          {neuPhase && (
            <Section
              title={t('v2.handover.section_incoming', 'Eingehend · Neue Projekte')}
              count={incoming.length}
              headerExtra={
                canReorder ? (
                  <button
                    type="button"
                    onClick={() => {
                      void confirmAction({
                        title: t(
                          'v2.handover.reihenfolge_zuruecksetzen',
                          'Reihenfolge zurücksetzen',
                        ),
                        body: t(
                          'v2.handover.reihenfolge_zuruecksetzen_frage',
                          'Die manuelle Reihenfolge wird verworfen. Danach gilt wieder die Sortierung nach Kunde.',
                        ),
                      }).then((ok) => {
                        if (ok) reihenfolge.zuruecksetzen();
                      });
                    }}
                    disabled={reihenfolge.isPending}
                    className="text-[11px] px-2 py-1 rounded-md"
                    style={{
                      background: 'none',
                      color: 'var(--ink-3)',
                      border: '1px solid var(--line-soft)',
                      cursor: reihenfolge.isPending ? 'not-allowed' : 'pointer',
                      opacity: reihenfolge.isPending ? 0.5 : 1,
                    }}
                  >
                    {t('v2.handover.reihenfolge_zuruecksetzen', 'Reihenfolge zurücksetzen')}
                  </button>
                ) : undefined
              }
            >
              {reihenfolge.isError && (
                <p className="text-sm" style={{ color: 'var(--danger)' }}>
                  {t(
                    'v2.handover.reihenfolge_laden_fehler',
                    'Reihenfolge konnte nicht geladen werden. Die Anzeige weicht möglicherweise von der geteilten Reihenfolge ab.',
                  )}
                </p>
              )}
              {incoming.length === 0 ? (
                <Card>
                  <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                    {t('v2.handover.section_empty', 'Keine Einträge.')}
                  </p>
                </Card>
              ) : (
                <>
                  {incoming.map((p) => (
                    <HandoverCard
                      key={p.project_id}
                      project={p}
                      phase={neuPhase}
                      kind="intake"
                      nextPhase={setupPhase ?? null}
                      dragHandlers={reihenfolge.handlers(p.project_id)}
                      dragging={reihenfolge.dragId === p.project_id}
                      dropHere={reihenfolge.dropTarget === p.project_id}
                    />
                  ))}
                  {/* Ablegefläche „ans Ende" — nur sichtbar, solange tatsächlich gezogen wird. */}
                  {reihenfolge.dragId !== null && (
                    <div
                      onDragOver={reihenfolge.endeHandlers.onDragOver}
                      onDrop={reihenfolge.endeHandlers.onDrop}
                      className="text-[11px] text-center py-2 rounded-md border border-dashed"
                      style={{
                        color: 'var(--ink-4)',
                        borderColor:
                          reihenfolge.dropTarget === REIHENFOLGE_ENDE_ZIEL
                            ? 'var(--primary)'
                            : 'var(--line-soft)',
                        background:
                          reihenfolge.dropTarget === REIHENFOLGE_ENDE_ZIEL
                            ? 'var(--primary-soft)'
                            : 'transparent',
                      }}
                    >
                      {t('v2.handover.reihenfolge_ans_ende', 'ans Ende ziehen')}
                    </div>
                  )}
                </>
              )}
            </Section>
          )}

          {handoverPhase && (
            <Section
              title={t('v2.handover.section_outgoing', 'Ausgehend · Handover-Projekte')}
              count={outgoing.length}
            >
              {outgoing.length === 0 ? (
                <Card>
                  <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                    {t('v2.handover.section_empty', 'Keine Einträge.')}
                  </p>
                </Card>
              ) : (
                outgoing.map((p) => (
                  <HandoverCard
                    key={p.project_id}
                    project={p}
                    phase={handoverPhase}
                    kind="outbound"
                    nextPhase={null}
                  />
                ))
              )}
            </Section>
          )}
        </div>
      )}

      <SegmentNotesBlock segment={segment} />
    </div>
  );
}

function Header({ count, segment }: { count: number; segment: string }) {
  const { t } = useTranslation();
  return (
    <header className="space-y-2 animate-fade-up">
      <div
        className="text-[11px] uppercase tracking-widest"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {t('v2.handover.eyebrow', 'Übergaben')}
      </div>
      <h1
        className="font-semibold tracking-tight"
        style={{
          fontFamily: 'var(--f-display)',
          color: 'var(--ink)',
          fontSize: 'clamp(22px, 2.4vw, 36px)',
          lineHeight: 1.1,
        }}
      >
        <span style={{ color: 'var(--primary)' }}>{count}</span>{' '}
        {t('v2.handover.headline_suffix', 'Übergaben · Segment: {{segment}}', {
          segment: segment || '—',
        })}
      </h1>
      <p className="text-sm max-w-3xl" style={{ color: 'var(--ink-2)' }}>
        {t(
          'v2.handover.subtitle',
          'Eingehend (Neu) + Ausgehend (Handover) für aktuelles Kundensegment. Inline editierbar.',
        )}
      </p>
    </header>
  );
}

function SegmentChipBar({
  segments,
  active,
  onChange,
  totalAllSegments,
}: {
  segments: string[];
  active: string;
  onChange: (s: string) => void;
  totalAllSegments: number;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="sticky top-0 z-10 -mx-2 px-2 py-3 flex items-center gap-2 flex-wrap"
      style={{ background: 'var(--bg)', borderBottom: '1px solid var(--line-soft)' }}
    >
      {segments.map((s) => {
        const isActive = s === active;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            className="text-xs px-3 py-1.5 rounded-full transition-colors"
            style={{
              background: isActive ? 'var(--primary-soft)' : 'var(--surface-2)',
              color: isActive ? 'var(--primary)' : 'var(--ink-2)',
              border: `1px solid ${isActive ? 'var(--primary)' : 'var(--line-soft)'}`,
              fontFamily: 'var(--f-mono)',
              cursor: 'pointer',
            }}
          >
            {s}
          </button>
        );
      })}
      <span
        className="ml-auto text-[11px]"
        style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
      >
        {t('v2.handover.total_all_segments', 'alle: {{n}}', { n: totalAllSegments })}
      </span>
    </div>
  );
}

function matchStyle(match: KfmlNameMatch): { color: string; background: string } {
  return match === 'scheint identisch'
    ? { color: 'var(--ok, #2e7d32)', background: 'var(--ok-soft, rgba(46,125,50,0.12))' }
    : { color: 'var(--warn)', background: 'var(--warn-soft)' };
}

function KfmlTodoRow({ item }: { item: KfmlTodoItem }) {
  const { t } = useTranslation();
  return (
    <li
      className="flex flex-col gap-0.5 py-1.5 border-t"
      style={{ borderColor: 'var(--line-soft)' }}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm" style={{ color: 'var(--ink)' }}>
          {item.text}
        </span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
          style={{
            color: 'var(--ink-3)',
            background: 'var(--surface-2)',
            fontFamily: 'var(--f-mono)',
          }}
        >
          {item.source === 'ki'
            ? t('v2.kfml.source_ki', 'KI')
            : t('v2.kfml.source_deterministic', 'automatisch')}
        </span>
        {item.match && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
            style={{ ...matchStyle(item.match), fontFamily: 'var(--f-mono)' }}
          >
            {item.match === 'scheint identisch'
              ? t('v2.kfml.match_identical', 'scheint identisch')
              : t('v2.kfml.match_diff', 'abweichend')}
          </span>
        )}
      </div>
      {item.current_sf_value && (
        <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
          {t('v2.kfml.col_current_sf', 'Aktueller SF-Wert')}: {item.current_sf_value}
        </span>
      )}
    </li>
  );
}

function KfmlProjectBlock({ project }: { project: KfmlProjectTodos }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span
          className="text-sm font-medium"
          style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)' }}
        >
          {project.project_key}
        </span>
        <span className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {project.customer_name}
        </span>
        {project.sf_url && (
          <a
            href={project.sf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] hover:underline"
            style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)' }}
          >
            ↗ {t('v2.kfml.sf_link', 'In Salesforce öffnen')}
          </a>
        )}
      </div>
      {project.todos.length === 0 ? (
        <p className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
          {t('v2.kfml.project_no_todos', 'Keine Todos für dieses Projekt.')}
        </p>
      ) : (
        <ul className="m-0 list-none p-0">
          {project.todos.map((item, i) => (
            <KfmlTodoRow key={`${item.source}-${i}-${item.text}`} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}

function KfmlResultPanel({
  segment,
  isLoading,
  result,
  onClose,
}: {
  segment: string;
  isLoading: boolean;
  result: KfmlTodoList | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>
            {t('v2.kfml.panel_title', 'KFML-Todos · Segment {{segment}}', {
              segment: segment || '—',
            })}
          </h2>
          <div className="ml-auto flex items-center gap-2">
            {result && (
              <BriefingExportActions text={result.markdown} fileName={`KFML-Todos_${segment}.md`} />
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-2 py-1 rounded"
              style={{
                color: 'var(--ink-3)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--f-mono)',
              }}
            >
              ✕ {t('v2.kfml.close', 'Schließen')}
            </button>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t(
              'v2.kfml.loading',
              'Todos werden erstellt – bei vielen Projekten dauert das einen Moment …',
            )}
          </p>
        ) : result && result.projects.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t(
              'v2.kfml.empty',
              'Für Segment {{segment}} liegen keine Intake-Notizen vor – keine Todos zu erstellen.',
              { segment: segment || '—' },
            )}
          </p>
        ) : result ? (
          <div className="space-y-5">
            {result.projects.map((p) => (
              <KfmlProjectBlock key={p.project_key} project={p} />
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function Section({
  title,
  count,
  children,
  headerExtra,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
  /** Optionale Aktion neben dem Titel (z. B. „Reihenfolge zurücksetzen") — steht
      NEBEN dem Auf-/Zuklapp-Knopf, nicht darin verschachtelt. */
  headerExtra?: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section className="space-y-3 animate-fade-up-d1">
      <div className="flex items-baseline gap-2 w-full">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-baseline gap-2 w-full text-left"
        >
          <span
            className="text-xs"
            style={{
              color: 'var(--ink-3)',
              transform: open ? 'none' : 'rotate(-90deg)',
              transition: 'transform 120ms',
              alignSelf: 'center',
            }}
          >
            ▾
          </span>
          <h2 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>
            {title}
          </h2>
          <span
            className="text-[11px]"
            style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
          >
            {count}
          </span>
        </button>
        {headerExtra && <div className="ml-auto">{headerExtra}</div>}
      </div>
      {open && <div className="space-y-4">{children}</div>}
    </section>
  );
}

function HandoverCard({
  project,
  phase,
  kind,
  nextPhase,
  dragHandlers,
  dragging = false,
  dropHere = false,
}: {
  project: Project;
  phase: Phase;
  kind: 'intake' | 'outbound';
  nextPhase: Phase | null;
  /** Nur im Eingang gesetzt (#115) — der Abschnitt „Ausgehend" ruft die Karte
      OHNE diese Props auf, dort bleibt nichts ziehbar. */
  dragHandlers?: EingangReihenfolgeHandlers;
  /** Diese Karte wird gerade selbst gezogen. */
  dragging?: boolean;
  /** Diese Karte ist aktuell das Ablegeziel eines Ziehvorgangs. */
  dropHere?: boolean;
}) {
  const { t } = useTranslation();
  const isReader = useReaderMode();
  const { user } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canEdit = !isReader;
  // #117: Blockierte Projekte wechseln die Phase nicht (Server: 409 project_blocked).
  const blockiert = project.is_blocked === true;
  const staffingPerms = useStaffingPermissions(project);

  // Übergabe-Felder folgen der echten Permission-Matrix (Issue #14): Intake = `edit_intake`
  // (u.a. MTB), Ausgang = `edit`. Reader bleiben generell außen vor.
  const scopeShape = {
    project_id: project.project_id,
    segment: project.segment as import('@projektverwaltung/shared').Segment,
    project_lead_user_id: project.project_lead_user_id ?? null,
    manager_user_id: project.manager_user_id ?? null,
    mtb_user_id: project.mtb_user_id ?? null,
    responsibility: project.responsibility ?? null,
    // #78: Der Zuteilungsschutz spielt an dieser Stelle keine Rolle — hier werden nur
    // edit/edit_intake/kfml_worklist_manage geprueft. `false` haelt das Verhalten unveraendert.
    phase_is_intake: project.phase_is_intake ?? false,
    lifecycle_status: project.lifecycle_status,
  };
  // #78: Zuteilungsschutz PL/Manager — dieselbe Rechtefrage + derselbe Hinweistext wie im
  // Projektdetail (`project-detail.tsx`, Right Rail): in der Eingangsphase nur die
  // kaufmaennische Leitung (und Voll-Admins), ausserhalb das normale Bearbeitungsrecht. Der
  // MTB ist davon ausdruecklich NICHT betroffen (bleibt bei `canEdit`). `scopeShape` traegt
  // bereits `phase_is_intake` — dieselbe Shape wie fuer `useCanAssignLeadManager` verlangt.
  const canAssign = useCanAssignLeadManager(scopeShape);
  const zuteilungGesperrt = canEdit && !canAssign;
  const zuteilungHinweis = t(
    'v2.detail.rail_assign_locked',
    'In Phase „Neu" nur durch die kaufmännische Leitung',
  );
  const canEditIntake =
    !isReader && user != null && canDoProjectAction(user, scopeShape, 'edit_intake');
  const canEditOutbound = !isReader && user != null && canDoProjectAction(user, scopeShape, 'edit');
  // KFML-Worklist-Haken (PV-016): eigenes Recht — NICHT staffingPerms.canManage.
  const canManageWorklist =
    !isReader && user != null && canDoProjectAction(user, scopeShape, 'kfml_worklist_manage');

  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const users = usersQuery.data?.items ?? [];

  // Klapp-Zustand flüchtig (Reload = wieder zu). Default eingeklappt (F13).
  const [expanded, setExpanded] = useState(false);

  const reviewKey = `${REVIEWED_STORAGE_PREFIX}${project.project_id}`;
  const [reviewed, setReviewed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(reviewKey) === '1';
  });
  const toggleReviewed = () => {
    setReviewed((v) => {
      const next = !v;
      if (next) {
        window.sessionStorage.setItem(reviewKey, '1');
        setExpanded(false); // "besprochen" → Karte klappt automatisch zu
      } else {
        window.sessionStorage.removeItem(reviewKey);
      }
      return next;
    });
  };

  // Staffing-Bereich je Karte separat klappbar, Default zu (F13, Issue #13).
  const staffingOpenKey = `${STAFFING_OPEN_STORAGE_PREFIX}${project.project_id}`;
  const [staffingOpen, setStaffingOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(staffingOpenKey) === '1';
  });
  const toggleStaffingOpen = () => {
    setStaffingOpen((v) => {
      const next = !v;
      if (typeof window !== 'undefined') {
        if (next) {
          window.sessionStorage.setItem(staffingOpenKey, '1');
        } else {
          window.sessionStorage.removeItem(staffingOpenKey);
        }
      }
      return next;
    });
  };

  // "◇ frei": noch unbesetzte Verantwortliche (PL/Manager/MTB)
  const hasFreeRole =
    !project.project_lead_user_id || !project.manager_user_id || !project.mtb_user_id;
  const budget = project.salesforce_data?.budget ?? null;
  const budgetLabel =
    typeof budget === 'number'
      ? new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        }).format(budget)
      : null;

  const invalidateProjectQueries = () => {
    void queryClient.invalidateQueries({ queryKey: ['v2-handover-projects'] });
    void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
    void queryClient.invalidateQueries({ queryKey: ['v2-mc-projects'] });
    void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
    void queryClient.invalidateQueries({ queryKey: ['v2-atlas-projects', 'all'] });
    // PV-052 L2 Fix-Runde 2: Befunde frisch laden. Der Abschluss wartet die Neuprüfung ab
    // (pruefeProjektSicher); Phasenwechsel/Patch stoßen sie nur an (fire-and-forget). Deckt patchProjectMut, moveMut
    // UND completeMut ab, da alle drei diesen gemeinsamen Helfer aufrufen.
    void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
    // #119: eine Notiz kfm. Leitung (HandoverForm, Feld kfml_notiz) kann hier den
    // KFML-Worklist-Haken automatisch setzen — ohne diese Zeile bliebe die Worklist-Seite
    // stehen, bis irgendetwas anderes sie invalidiert oder neu geladen wird.
    void queryClient.invalidateQueries({ queryKey: ['v2-kfml-worklist'] });
  };

  const patchProjectMut = useMutation({
    mutationFn: (input: {
      project_lead_user_id?: string | null;
      manager_user_id?: string | null;
      mtb_user_id?: string | null;
    }) => api.patchProject(project.project_id, input),
    onSuccess: () => {
      invalidateProjectQueries();
      toast.success(t('v2.handover.toast_assign_saved', 'Zuweisung gespeichert'));
    },
    onError: (err) =>
      toast.error(
        t('v2.handover.toast_assign_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const moveMut = useMutation({
    mutationFn: () => {
      if (!nextPhase) throw new Error('next_phase_missing');
      return api.movePhase(project.project_id, nextPhase.phase_id);
    },
    onSuccess: () => {
      invalidateProjectQueries();
      void queryClient.invalidateQueries({ queryKey: ['v2-phases'] });
      toast.success(t('v2.handover.toast_moved', 'Verschoben auf Setup'));
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.payload as { error?: string } | null)?.error === 'project_blocked'
      ) {
        // #117: Block inzwischen anderswo gesetzt — derselbe Hinweis wie im Phase Board.
        invalidateProjectQueries();
        toast.error(
          t('v2.board.move_blocked_project', 'Projekt ist blockiert'),
          t('v2.board.move_blocked_project_hint', 'Erst Block lösen, dann die Phase wechseln.'),
        );
      } else if (err instanceof ApiError && err.isHardStop()) {
        toast.error(
          t('v2.handover.toast_move_blocked', 'Phasenwechsel blockiert'),
          t('v2.handover.toast_move_blocked_hint', 'Im Project-Detail erledigen.'),
        );
      } else {
        toast.error(
          t('v2.handover.toast_move_failed', 'Verschieben fehlgeschlagen'),
          err instanceof Error ? err.message : 'Fehler',
        );
      }
    },
  });

  const completeMut = useMutation({
    mutationFn: () => api.completeProject(project.project_id),
    onSuccess: () => {
      invalidateProjectQueries();
      toast.success(t('v2.handover.toast_completed', 'Projekt abgeschlossen'));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.isHardStop()) {
        toast.error(
          t('v2.handover.toast_complete_blocked', 'Abschluss blockiert'),
          t('v2.handover.toast_complete_blocked_hint', 'Im Project-Detail erledigen.'),
        );
      } else {
        toast.error(
          t('v2.handover.toast_complete_failed', 'Abschließen fehlgeschlagen'),
          err instanceof Error ? err.message : 'Fehler',
        );
      }
    },
  });

  const outboundRequired = new Set(phase.required_fields ?? []);
  // Ein `draggable="true"`-Vorfahre unterdrückt in Chrome/Firefox die Textauswahl aller
  // nicht editierbaren Nachkommen — im aufgeklappten Zustand ließen sich Projektnummer,
  // Kundenname und Notiztexte sonst nicht mehr markieren. Deshalb nur ziehbar, solange die
  // Karte zu ist; der Ziehgriff wird konsequenterweise nur gezeigt, wenn auch wirklich
  // gezogen werden kann (siehe Gesamt-Review #115).
  const kannGezogenWerden = (dragHandlers?.draggable ?? false) && !expanded;

  // #83: Picker PL/Manager/MTB — im Eingang Slot im Intake-Block, im Ausgang eigene Zeile.
  // #78: PL/Manager tragen denselben Zuteilungsschutz wie im Projektdetail (`canAssign` +
  // `disabledReason` + `required`) — das gilt hier fuer BEIDE Karten-Arten (Eingang UND
  // Ausgang), weil `verantwortlichePicker` an beiden Stellen verwendet wird (Slot im
  // Intake-Block bzw. eigene Zeile im Ausgang). Der MTB bleibt bewusst ungeschuetzt
  // (nur `canEdit`, nicht `required`) — siehe Kommentar in `project-detail.tsx`.
  const verantwortlichePicker = (
    <>
      <AssignableRow
        label={t('v2.detail.rail_project_lead', 'Project Lead')}
        userId={project.project_lead_user_id}
        users={users}
        canEdit={canEdit && canAssign}
        disabledReason={zuteilungGesperrt ? zuteilungHinweis : undefined}
        required
        onAssign={(uid) => patchProjectMut.mutate({ project_lead_user_id: uid })}
      />
      <AssignableRow
        label={t('v2.detail.rail_manager', 'Manager')}
        userId={project.manager_user_id}
        users={users}
        canEdit={canEdit && canAssign}
        disabledReason={zuteilungGesperrt ? zuteilungHinweis : undefined}
        required
        onAssign={(uid) => patchProjectMut.mutate({ manager_user_id: uid })}
      />
      <AssignableRow
        label={t('v2.detail.rail_mtb', 'MTB')}
        userId={project.mtb_user_id}
        users={users}
        canEdit={canEdit}
        onAssign={(uid) => patchProjectMut.mutate({ mtb_user_id: uid })}
      />
    </>
  );

  return (
    <div
      // Die eigentlichen Ziehattribute (`draggable`, `onDragStart` …) hängen am
      // äußeren Container, nicht an einem separaten Griff-Element — Muster wie
      // die ganze Karte in `phase-board.tsx` (dort `<li draggable>`).
      draggable={kannGezogenWerden}
      onDragStart={dragHandlers?.onDragStart}
      onDragEnd={dragHandlers?.onDragEnd}
      onDragOver={dragHandlers?.onDragOver}
      onDragLeave={dragHandlers?.onDragLeave}
      onDrop={dragHandlers?.onDrop}
      style={{
        opacity: reviewed ? 0.6 : dragging ? 0.4 : 1,
        transition: 'opacity 120ms ease-out',
        // Einfügelinie an der Oberkante der Zielkarte statt Vollrahmen — ein Vollrahmen UM
        // die Karte suggeriert „vor dieser Karte", wirkt aber „nach der vorherigen"; beim
        // Ablegen auf der unmittelbar folgenden Karte war dadurch keine Änderung sichtbar,
        // obwohl ein Ablegeplatz bestätigt wurde (Gesamt-Review #115).
        borderTop: dropHere ? '3px solid var(--primary)' : 'none',
        borderRadius: 12,
      }}
    >
      <Card padding={false}>
        {/* Dichte Kopfzeile — gesamte Zeile klickt auf/zu (F13) */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded((v) => !v);
            }
          }}
          className="w-full flex items-center gap-2 p-4 cursor-pointer select-none"
        >
          {kannGezogenWerden && (
            // Reiner Sicht-/Griff-Hinweis — die Ziehattribute selbst sitzen am
            // äußeren Container (s. o.), damit die ganze Karte greifbar bleibt. Kein
            // eigener onClick: ein bloßer Klick (ohne Ziehen) darf ruhig wie ein Klick
            // auf die Kopfzeile wirken (auf-/zuklappen) — das ist kein Bedienfehler,
            // nur ein harmloser Nebeneffekt, und vermeidet einen Klick-Handler ohne
            // Tastatur-Äquivalent (lint/a11y/useKeyWithClickEvents). Nur sichtbar, wenn
            // auch wirklich gezogen werden kann (aufgeklappte Karte ist nicht ziehbar).
            <span
              aria-label={t('v2.handover.reihenfolge_griff', 'Zum Umsortieren ziehen')}
              title={t('v2.handover.reihenfolge_griff', 'Zum Umsortieren ziehen')}
              className="text-xs shrink-0"
              style={{ color: 'var(--ink-4)', cursor: 'grab' }}
            >
              ⠿
            </span>
          )}
          <span
            className="text-xs shrink-0"
            style={{
              color: 'var(--ink-3)',
              transform: expanded ? 'none' : 'rotate(-90deg)',
              transition: 'transform 120ms',
            }}
          >
            ▾
          </span>
          <Link
            to={`/projects/${project.project_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm font-medium hover:underline shrink-0"
            style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)' }}
          >
            ★ {project.project_key}
          </Link>
          {/* Lesereihenfolge (#83, Abnahme): links WAS (Kunde · Titel), rechts Kennzahlen.
              Keine Phasen-Plakette — der Abschnitt „Eingehend"/„Ausgehend" sagt die Phase schon. */}
          <span className="text-sm font-semibold shrink-0" style={{ color: 'var(--ink)' }}>
            {project.customer_name}
          </span>
          <span
            className="text-sm truncate min-w-0 flex-1"
            style={{ color: 'var(--ink-2)' }}
            title={project.name}
          >
            {project.name}
          </span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {project.ms_classification && (
              <span
                className="text-[10px] uppercase tracking-[0.08em] hidden lg:inline"
                style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
              >
                {project.ms_classification}
              </span>
            )}
            <span
              className="text-[10px] hidden lg:inline"
              style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
            >
              {project.segment}
            </span>
            {hasFreeRole && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{
                  background: 'var(--warn-soft)',
                  color: 'var(--warn)',
                  fontFamily: 'var(--f-mono)',
                }}
                title={t(
                  'v2.handover.free_roles_hint',
                  'Verantwortliche unbesetzt (PL/Manager/MTB)',
                )}
              >
                ◇ {t('v2.handover.free', 'frei')}
              </span>
            )}
            {budgetLabel && (
              // Feste Mindestbreite + rechtsbündig: die Beträge stehen kartenübergreifend
              // untereinander und lassen sich als Spalte überfliegen.
              <span
                className="text-sm font-semibold tabular-nums text-right min-w-[6.5rem]"
                style={{ color: 'var(--ink)', fontFamily: 'var(--f-mono)' }}
              >
                {budgetLabel}
              </span>
            )}
            <label
              className="flex items-center gap-1.5 text-xs cursor-pointer"
              style={{ color: 'var(--ink-3)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <input type="checkbox" checked={reviewed} onChange={toggleReviewed} />
              <span>{t('v2.handover.reviewed', 'besprochen')}</span>
            </label>
          </div>
        </div>

        {expanded && (
          <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: 'var(--line-soft)' }}>
            <div className="pt-2" />

            {/* Salesforce-Daten */}
            <SFInfoCard
              data={project.salesforce_data}
              salesforceUrl={project.salesforce_url}
              embedded
            />
            <HandoverSfFilePanel
              projectId={project.project_id}
              salesforceUrl={project.salesforce_url ?? null}
            />

            <div style={{ borderTop: '1px solid var(--line-soft)' }} />

            {/* Verantwortliche — im Eingang wandern sie in den Intake-Block (#83), im Ausgang
                (kein Intake-Block) bleibt die Zeile. */}
            {kind === 'outbound' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">{verantwortlichePicker}</div>
            )}

            {/* Bereich-Override */}
            <div className="flex items-center gap-3">
              <span className="text-sm" style={{ color: 'var(--ink-3)' }}>
                {t('v2.detail.ov_bereich', 'Bereich')}
              </span>
              <ResponsibilityPicker
                projectId={project.project_id}
                value={project.responsibility ?? null}
                canEdit={canEdit}
              />
            </div>

            <div style={{ borderTop: '1px solid var(--line-soft)' }} />

            {/* Ausgangs-Übergabe-Felder — nur Ausgang. Der Eingang nutzt
                ausschließlich den strukturierten Intake-Notizen-Block (Issue #14). */}
            {kind === 'outbound' && (
              <HandoverForm
                kind="outbound"
                title={t('v2.handover.outbound_title', 'Ausgangs-Übergabe')}
                hint={t(
                  'v2.detail.handover_outbound_hint',
                  'Pflicht beim Phasenwechsel auf Handover.',
                )}
                fields={[...OUTBOUND_FIELDS]}
                required={outboundRequired}
                initial={
                  (project.handover_ready_fields ?? {}) as Record<string, string | undefined>
                }
                projectId={project.project_id}
                canEdit={canEditOutbound}
                isReader={isReader}
                onSaved={invalidateProjectQueries}
                embedded
              />
            )}

            {/* Strukturierter Intake-Notizen-Block (Issue #14, WP-4) — nur Eingang */}
            {kind === 'intake' && (
              <IntakeNotesBlock
                projectId={project.project_id}
                intakeFields={project.intake_handover_fields ?? {}}
                canEdit={canEditIntake}
                isReader={isReader}
                onSaved={invalidateProjectQueries}
                embedded
                verantwortlich={verantwortlichePicker}
              />
            )}

            <div style={{ borderTop: '1px solid var(--line-soft)' }} />

            {/* Staffing — eigener Klapp-Bereich, Default zu (Issue #13) */}
            <div>
              <button
                type="button"
                onClick={toggleStaffingOpen}
                aria-expanded={staffingOpen}
                className="flex items-center gap-2 w-full text-left mb-2"
              >
                <span
                  className="text-xs"
                  style={{
                    color: 'var(--ink-3)',
                    transform: staffingOpen ? 'none' : 'rotate(-90deg)',
                    transition: 'transform 120ms',
                  }}
                >
                  ▾
                </span>
                <h3
                  className="text-[11px] uppercase tracking-[0.14em]"
                  style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                >
                  {t('v2.handover.staffing_title', 'Staffing-Bedarfe')}
                </h3>
              </button>
              {/* #158: dieselbe Staffing-Maske wie Staffingseite und Projektdetail (lädt selbst);
                  die Kompaktform folgt mit #159. */}
              {staffingOpen && <StaffingMaske projectId={project.project_id} project={project} />}
            </div>

            <div style={{ borderTop: '1px solid var(--line-soft)' }} />

            {/* Aktionsleiste: Fälligkeit + KFML-Haken neben dem Aktionsknopf (#83).
                ACHTUNG: die beiden Controls hängen an VERSCHIEDENEN Rechten — „Wieder fällig am"
                an staffing_manage (schließt den PL auf eigenen Projekten ein), der Haken an
                kfml_worklist_manage (schließt ihn aus). Nicht vereinheitlichen. */}
            <div
              data-testid="handover-action-bar"
              className="flex items-center justify-end gap-3 flex-wrap"
            >
              {/* Rechtsbündig direkt am Knopf, nicht über die Breite verteilt (#83, Abnahme). */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* `<span>` statt `<label>`: kein eigenes Formularfeld hier — die eigentliche
                    Eingabe steckt in `KlFollowupDateEditor` (biome noLabelWithoutControl). */}
                <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
                  {t('v2.staffing.followup', 'Wieder fällig am')}
                </span>
                <KlFollowupDateEditor
                  projectId={project.project_id}
                  value={
                    (project as unknown as { kl_followup_date?: string | null }).kl_followup_date ??
                    null
                  }
                  canEdit={staffingPerms.canManage}
                />
                <KfmlWorklistToggle
                  projectId={project.project_id}
                  value={project.kfml_worklist ?? false}
                  canEdit={canManageWorklist}
                  onSaved={invalidateProjectQueries}
                  // #136: „Eingehend" = Eingangsphase — die Worklist zeigt das Projekt erst ab Setup.
                  wartetAufSetup={kind === 'intake'}
                />
              </div>
              {kind === 'intake' ? (
                <button
                  type="button"
                  onClick={() => moveMut.mutate()}
                  disabled={!canEdit || !nextPhase || moveMut.isPending || blockiert}
                  title={
                    isReader
                      ? t('v2.reader.tooltip', 'Sie haben Leserechte')
                      : blockiert
                        ? t('v2.detail.move_phase_blocked_tooltip', 'Erst Block lösen')
                        : undefined
                  }
                  className="text-xs px-3 py-2 rounded-md"
                  style={{
                    background: 'var(--primary)',
                    color: 'var(--on-primary)',
                    border: 'none',
                    opacity: !canEdit || !nextPhase || moveMut.isPending || blockiert ? 0.5 : 1,
                    cursor: !canEdit || blockiert ? 'not-allowed' : 'pointer',
                  }}
                >
                  {moveMut.isPending
                    ? t('v2.handover.btn_move_pending', 'Verschiebt…')
                    : t('v2.handover.btn_move_to_setup', '→ Move auf Setup')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => completeMut.mutate()}
                  disabled={!canEdit || completeMut.isPending}
                  title={isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined}
                  className="text-xs px-3 py-2 rounded-md"
                  style={{
                    background: 'var(--primary)',
                    color: 'var(--on-primary)',
                    border: 'none',
                    opacity: !canEdit || completeMut.isPending ? 0.5 : 1,
                    cursor: !canEdit ? 'not-allowed' : 'pointer',
                  }}
                >
                  {completeMut.isPending
                    ? t('v2.handover.btn_complete_pending', 'Schließt ab…')
                    : t('v2.handover.btn_complete', '→ Projekt abschließen')}
                </button>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
